import {centroid} from '@turf/turf';
import * as geojson from 'geojson';
import {Expression} from 'mapbox-gl';
import React, {useEffect, useMemo} from 'react';

import {NotesActions, NotesState, StateApiNote} from 'app/stores/NotesStore';
import colors from 'app/styles/colors.json';
import {NOTE_TYPE_AGENT} from 'app/utils/constants';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as makeLayers from 'app/utils/mapUtils/makeLayers';
import * as noteUtils from 'app/utils/noteUtils';

import MapContent from './MapContent';
import {useMapCursor} from './MapCursorProvider';

const NOTES_MARKERS_ID = 'notes';
const NOTES_POLYGONS_ID = 'notes_polygons';

const PENDING_GEOMETRY_SOURCE_ID = 'pending_note_geometry';
const PENDING_GEOMETRY_MARKER_LAYER_ID = 'pending_note_geometry_markers';
const PENDING_GEOMETRY_POLYGON_LAYER_ID = 'pending_note_geometry_polygons';

const MAP_POINTER = 'map_pointer';

export interface Props {
  map: mapboxgl.Map;
  notesState: NotesState;
  /** Null renders the notes without any click or hover handling. */
  notesActions: NotesActions | null;
  isMapLoaded: boolean;
  isVisible: boolean;
  onClickNoteMarkerCallback?: (
    geometry: geojson.GeoJSON | null,
    note: StateApiNote | undefined
  ) => void;
  filterNotesCallback?: (note: StateApiNote) => boolean;
  /**
   * When set, every agent observation outline is visible until one is focused, at
   * which point only the focused outline shows. Only enabled while reviewing a
   * run's observations in the agent pane.
   */
  showAllAgentObservations?: boolean;
  /**
   * Turns off click-to-focus while another map tool owns the pointer, e.g. drawing
   * "find more like this" boxes, where a drag that ends on a note would otherwise
   * focus it and swap the agent pane to that note's run.
   */
  disableClicks?: boolean;
}

const IS_HOVERED_EXPRESSION = ['==', ['get', 'isHovered'], true];
const IS_FOCUSED_EXPRESSION = ['==', ['get', 'isFocused'], true];
const IS_NOT_FOCUSED_EXPRESSION = ['==', ['get', 'isFocused'], false];
const IS_POLYGON_POINT_EXPRESSION = ['==', ['get', 'isPolygon'], true];
const IS_PENDING_EXPRESSION = ['==', ['get', 'isPending'], true];
const IS_NOTE_TYPE_USER_EXPRESSION = ['==', ['get', 'noteType'], 'user'];
const IS_NOTE_TYPE_ALERT_EXPRESSION = ['==', ['get', 'noteType'], 'alert'];
const IS_NOTE_TYPE_AGENT_EXPRESSION = ['==', ['get', 'noteType'], 'agent'];

/**
 * Clicking on a feature from the notes marker layer focuses it.
 */
function handleClickNoteMarker(
  notesActions: NotesActions,
  notes: NotesState['notes'],
  onClickNoteMarkerCallback: (
    geometry: geojson.GeoJSON | null,
    note: StateApiNote | undefined
  ) => void,
  ev: mapboxgl.MapLayerMouseEvent
) {
  const feature = ev.features?.[0];
  const properties = feature?.properties;

  if (feature && properties) {
    notesActions.focusNote(properties.id);
    onClickNoteMarkerCallback(
      feature.geometry,
      notes.find(({id}) => id === properties.id)
    );
  }
}

/**
 * Clicking on a note polygon focuses it, but only for agent notes. Agent
 * observations render as outlined polygons with no marker (see the marker
 * layer's filter), so the polygon is the only thing there is to click. Other
 * note types have a marker that handles focusing, and focusing a user note from
 * here would be ambiguous (user notes live in both the notes and agent panes).
 */
function handleClickNotePolygon(
  notesActions: NotesActions,
  notes: NotesState['notes'],
  onClickNoteMarkerCallback: (
    geometry: geojson.GeoJSON | null,
    note: StateApiNote | undefined
  ) => void,
  ev: mapboxgl.MapLayerMouseEvent
) {
  const feature = ev.features?.[0];
  const properties = feature?.properties;

  if (feature && properties && properties.noteType === NOTE_TYPE_AGENT) {
    notesActions.focusNote(properties.id);
    onClickNoteMarkerCallback(
      feature.geometry,
      notes.find(({id}) => id === properties.id)
    );
  }
}

/**
 * Clicking on a feature from the pending geometry layer re-opens editing of it.
 */
function handleClickPendingGeometry(notesActions: NotesActions) {
  notesActions.startEditingPendingGeometry();
}

// We use mousemove instead of mouseenter because the latter doesn’t trigger if
// you move from one feature to another continuously (e.g. the markers are
// clumped together). notesStore is tolerant of not updating its state if we
// call hoverNote redundantly.
function handleMouseMoveNoteMarker(notesActions: NotesActions, ev: mapboxgl.MapLayerMouseEvent) {
  if (ev.originalEvent.buttons === 1) return; // disable hover on drag
  const properties = ev.features?.[0].properties;
  if (properties) {
    notesActions.hoverNote(properties.id);
  }
}

/**
 * Declarative component to add the geometry from a list of notes to the map.
 */
const NoteVector: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  map,
  isMapLoaded,
  isVisible,
  notesState,
  notesActions,
  onClickNoteMarkerCallback = () => {},
  filterNotesCallback,
  showAllAgentObservations = false,
  disableClicks = false,
}) => {
  const {addPendingNoteGeometryMode, notes, focusedNoteId} = notesState;

  // Show a pointer over agent-note polygons, since those are the only note
  // geometries that are click-to-focus (see handleClickNotePolygon). Skip it in
  // pending-geometry mode or with disableClicks, where the polygon click handler is disabled.
  const isAgentNoteFeature = React.useCallback(
    (ev: mapboxgl.MapLayerMouseEvent) => ev.features?.[0]?.properties?.noteType === NOTE_TYPE_AGENT,
    []
  );
  useMapCursor(
    addPendingNoteGeometryMode || disableClicks || !notesActions ? [] : [NOTES_POLYGONS_ID],
    'pointer',
    isAgentNoteFeature
  );

  const filteredNotes = filterNotesCallback ? notes.filter(filterNotesCallback) : notes;

  // track whether at least one note is focused so that we can style the unfocused note markers
  const anyNoteIsFocused = !!focusedNoteId;

  // Once a rendered observation is focused, collapse from all outlines to just the focused one.
  const showAllAgentObservationOutlines =
    showAllAgentObservations && !filteredNotes.some((n) => n.id === focusedNoteId);

  const makeNotesLayers = React.useCallback(
    (anyNoteIsFocused: boolean, showAllAgentObservations: boolean, isAgentRunView: boolean) => {
      return [
        makeLineLayer(NOTES_POLYGONS_ID, {showAllAgentObservations, isAgentRunView}),
        // We use the same source for pending notes, since we’re not doing
        // the shenanigans around hiding polygons when they’re not being
        // hovered or moving the marker around.
        makeLineLayer(PENDING_GEOMETRY_SOURCE_ID, {
          layerId: PENDING_GEOMETRY_POLYGON_LAYER_ID,
        }),
        makeMarkerSymbolLayer(NOTES_MARKERS_ID, undefined, anyNoteIsFocused),
        makeMarkerSymbolLayer(
          PENDING_GEOMETRY_SOURCE_ID,
          {
            layerId: PENDING_GEOMETRY_MARKER_LAYER_ID,
          },
          anyNoteIsFocused
        ),
      ];
    },
    []
  );

  useEffect(() => {
    if (!notesActions) {
      return;
    }
    if (addPendingNoteGeometryMode || disableClicks) {
      return;
    }

    // Need to save these out so we can reference them in "off"
    const onClickNoteMarker = handleClickNoteMarker.bind(
      null,
      notesActions,
      notes,
      onClickNoteMarkerCallback
    );
    const onClickNotePolygon = handleClickNotePolygon.bind(
      null,
      notesActions,
      notes,
      onClickNoteMarkerCallback
    );
    const onClickPendingGeometry = handleClickPendingGeometry.bind(null, notesActions);
    const onMouseMoveNoteMarker = handleMouseMoveNoteMarker.bind(null, notesActions);
    const onMouseLeaveNoteMarker = () => notesActions.hoverNote(null);

    map.on('click', NOTES_MARKERS_ID, onClickNoteMarker);
    map.on('click', NOTES_POLYGONS_ID, onClickNotePolygon);
    map.on('click', PENDING_GEOMETRY_POLYGON_LAYER_ID, onClickPendingGeometry);
    map.on('click', PENDING_GEOMETRY_MARKER_LAYER_ID, onClickPendingGeometry);
    map.on('mousemove', NOTES_MARKERS_ID, onMouseMoveNoteMarker);
    map.on('mouseleave', NOTES_MARKERS_ID, onMouseLeaveNoteMarker);

    return () => {
      map.off('click', NOTES_MARKERS_ID, onClickNoteMarker);
      map.off('click', NOTES_POLYGONS_ID, onClickNotePolygon);
      map.off('click', PENDING_GEOMETRY_POLYGON_LAYER_ID, onClickPendingGeometry);
      map.off('click', PENDING_GEOMETRY_MARKER_LAYER_ID, onClickPendingGeometry);
      map.off('mousemove', NOTES_MARKERS_ID, onMouseMoveNoteMarker);
      map.off('mouseleave', NOTES_MARKERS_ID, onMouseLeaveNoteMarker);
    };
  }, [
    map,
    addPendingNoteGeometryMode,
    disableClicks,
    notes,
    notesActions,
    onClickNoteMarkerCallback,
  ]);

  const sources = useMemo(
    () => [
      {
        id: NOTES_MARKERS_ID,
        source: makeNotesPointSource({...notesState, notes: filteredNotes}),
      },
      {
        id: NOTES_POLYGONS_ID,
        source: makeNotesPolygonSource({...notesState, notes: filteredNotes}),
      },
      {id: PENDING_GEOMETRY_SOURCE_ID, source: makePendingNoteSource(notesState)},
    ],
    [notesState, filteredNotes]
  );

  return (
    <MapContent
      map={map}
      isMapLoaded={isMapLoaded}
      sources={sources}
      layers={
        isVisible
          ? makeNotesLayers(
              anyNoteIsFocused,
              showAllAgentObservationOutlines,
              showAllAgentObservations
            )
          : []
      }
    />
  );
};

export default NoteVector;

/**
 * Given the currently focused and hovered notes, generates a function that
 * updates the "isFocused" and "isHovered" properties of a list of features.
 */
const addFocusHoverProperties =
  ({focusedNoteId, hoveredNoteId}: Pick<NotesState, 'focusedNoteId' | 'hoveredNoteId'>) =>
  (features: geojson.Feature<geojson.Geometry, noteUtils.NoteFeatureProperties>[]) =>
    features.map((f) => ({
      ...f,
      properties: {
        ...f.properties,
        isFocused: f.properties.id === focusedNoteId,
        isHovered: f.properties.id === hoveredNoteId,
      },
    }));

const findNorthernmostPoint = (polygon: geojson.Polygon | geojson.MultiPolygon) => {
  // We sort the polygon points to find the northernmost one to use as our
  // anchor for the label. This means that if we put the label above this point,
  // it won’t intersect with any of the polygon. (Since we do fix the map’s
  // orientation so north === up.)

  let points: geojson.Position[] = [];

  if (polygon.type === 'MultiPolygon') {
    // if shape is a MultiPolygon get the northernmost point of each of the polygons
    // within it
    points = polygon.coordinates.map((shape) => {
      const shapePoints = [...shape[0]];
      shapePoints.sort((a, b) => b[1] - a[1]);

      return shapePoints[0];
    });
  } else {
    points = [...polygon.coordinates[0]];
  }

  points.sort((a, b) => b[1] - a[1]);
  return geoJsonUtils.point(points[0] as [number, number], {}).geometry;
};

/**
 * Function that turns all polygon features into their particular points. If the
 * feature is focused, puts the point on the first vertex.
 */
const replacePolygonFeatures = (
  features: geojson.Feature<geojson.Geometry, noteUtils.NoteFeatureProperties>[]
) =>
  features.map((f) =>
    f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
      ? {
          ...f,
          properties: {
            ...f.properties,
            isPolygon: true,
          },
          geometry: f.properties.isFocused
            ? findNorthernmostPoint(f.geometry)
            : centroid(f.geometry).geometry,
        }
      : f
  );

const featureCollectionToSource = (fc: geojson.FeatureCollection): mapboxgl.AnySourceData => ({
  type: 'geojson',
  data: fc,
});

/**
 * Makes a MapboxGL source with GeoJSON data from the notes’ geometries.
 * Converts all polygonal note geometries to a point.
 */
const makeNotesPointSource = ({
  notes,
  focusedNoteId,
  hoveredNoteId,
}: NotesState): mapboxgl.AnySourceData =>
  featureCollectionToSource(
    geoJsonUtils.featureCollection(
      replacePolygonFeatures(
        addFocusHoverProperties({
          focusedNoteId,
          hoveredNoteId,
        })(noteUtils.getNoteFeatures(notes))
      )
    )
  );

/**
 * Makes a MapboxGL source with any note polygons.
 */
const makeNotesPolygonSource = ({
  notes,
  focusedNoteId,
  hoveredNoteId,
}: NotesState): mapboxgl.SourceSpecification =>
  featureCollectionToSource(
    geoJsonUtils.featureCollection(
      addFocusHoverProperties({focusedNoteId, hoveredNoteId})(
        ((features) =>
          features.filter(
            (f) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
          ))(noteUtils.getNoteFeatures(notes))
      )
    )
  );

/**
 * Creates a MapboxGL source for any pending note geometry. We only render
 * polygons if we’re not editing them, since the mapbox-gl-draw control renders
 * them if they are being edited.
 */
const makePendingNoteSource = ({
  pendingNoteGeometryFeature,
  addPendingNoteGeometryMode,
}: NotesState): mapboxgl.AnySourceData => {
  const features: geojson.Feature<geojson.Geometry, any>[] = [];

  if (
    pendingNoteGeometryFeature &&
    addPendingNoteGeometryMode !== 'polygon' &&
    addPendingNoteGeometryMode !== 'rect'
  ) {
    features.push({
      ...pendingNoteGeometryFeature,
      properties: {
        ...pendingNoteGeometryFeature.properties,
        isPending: true,
        isPolygon:
          pendingNoteGeometryFeature.geometry.type === 'Polygon' ||
          pendingNoteGeometryFeature.geometry.type === 'MultiPolygon',
      },
    });
  }

  return featureCollectionToSource(geoJsonUtils.featureCollection(features));
};

function makeMarkerSymbolLayer(
  sourceId: string,
  opts: {layerId?: string} = {},
  anyNoteIsFocused: boolean
): mapboxgl.SymbolLayer {
  return {
    id: opts.layerId || sourceId,
    maxzoom: 22,
    minzoom: 2,
    type: 'symbol',
    source: sourceId,
    filter: [
      '!',
      [
        'any',
        ['all', IS_PENDING_EXPRESSION, IS_POLYGON_POINT_EXPRESSION],
        IS_NOTE_TYPE_AGENT_EXPRESSION,
      ],
    ],
    layout: {
      'icon-size': 0.5,
      'icon-image': MAP_POINTER,
      'icon-anchor': ['case', IS_POLYGON_POINT_EXPRESSION, 'center', 'bottom'],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-offset': [
        'case',
        // We don’t want the icon to overlap the polygon, so we move it up a
        // bit. The icon is positioned at the top-most vertex of the polygon.
        ['all', IS_POLYGON_POINT_EXPRESSION, IS_FOCUSED_EXPRESSION],
        ['literal', [0, -40]],
        ['literal', [0, 0]],
      ],
    },
    paint: {
      'icon-opacity': [
        'case',
        // if a map pointer is focused, it should never be transparent
        IS_FOCUSED_EXPRESSION,
        1,
        // else when we hover over a map pointer, show it semi-transparent
        IS_HOVERED_EXPRESSION,
        0.7,
        // if any note is focused, but not this note, show the pointer VERY
        // transparent (will still get the hover semi-transparency)
        ['all', IS_NOT_FOCUSED_EXPRESSION, anyNoteIsFocused],
        0.25,
        // default: if NOTHING is focused, just show everything at full opacity
        1,
      ],
    },
  };
}

// When reviewing an agent run, the saved observations come through as `user`
// notes. In that view we color them green so they stand out from the new
// (unsaved) agent observations, which stay purple. Everywhere else user notes
// are their usual yellow.
const makeColorExpression = (isAgentRunView: boolean): Expression => [
  'case',
  IS_NOTE_TYPE_USER_EXPRESSION,
  isAgentRunView ? colors.brightGreen : colors.yellow,
  IS_NOTE_TYPE_ALERT_EXPRESSION,
  colors.darkRed,
  // Agent notes stay purple regardless of focus so they read consistently as
  // agent observations.
  IS_NOTE_TYPE_AGENT_EXPRESSION,
  colors.purple,
  colors.primary,
];

/**
 * Returns a layer for styling polygonal note features. These are hidden
 * normally (leaving just the marker), but become apparent on hover and focus.
 */
function makeLineLayer(
  sourceId: string,
  opts: {layerId?: string; showAllAgentObservations?: boolean; isAgentRunView?: boolean} = {}
): mapboxgl.LineLayerSpecification {
  return {
    id: opts.layerId || sourceId,
    maxzoom: 22,
    minzoom: 2,
    // We don’t have to filter on geometry type because mapbox-gl won’t try to
    // render a "line" layer for "point" values.
    ...makeLayers.makeLine({
      color: makeColorExpression(opts.isAgentRunView ?? false),
      width: 4,
      // With showAllAgentObservations, every observation outline stays visible —
      // both new (agent) and saved (user) run notes — otherwise outlines only
      // show when focused or pending.
      opacity: [
        'case',
        [
          'any',
          IS_FOCUSED_EXPRESSION,
          IS_PENDING_EXPRESSION,
          ...(opts.showAllAgentObservations
            ? [IS_NOTE_TYPE_AGENT_EXPRESSION, IS_NOTE_TYPE_USER_EXPRESSION]
            : []),
        ],
        1,
        0,
      ],
    }),
    source: sourceId,
  };
}
